// Workflows Service - CRUD operations con Firestore
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Node, Edge } from '@xyflow/react';
import type { ExcelFileMetadata } from '../types/excel';
import { normalizeWorkflowGraph } from './workflowNormalization';

export interface Workflow {
  id: string;
  userId: string;
  name: string;
  description?: string;
  nodes: Node[];
  edges: Edge[];
  excelFiles?: ExcelFileMetadata[]; // Archivos Excel globales del workflow
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastExecutedAt?: Timestamp;
  executionCount: number;
  successCount: number;
  failureCount: number;
  isPublic: boolean;
  tags: string[];
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  nodes: Node[];
  edges: Edge[];
  excelFiles?: ExcelFileMetadata[];
  tags?: string[];
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  nodes?: Node[];
  edges?: Edge[];
  excelFiles?: ExcelFileMetadata[];
  tags?: string[];
}

/**
 * H-F010: Helper para validar que un valor sea un Timestamp de Firestore.
 * Retorna null si el valor no es un Timestamp valido.
 */
function safeTimestamp(value: unknown): Timestamp | null {
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as Timestamp).toDate === 'function'
  ) {
    return value as Timestamp;
  }
  return null;
}

/**
 * Valida y construye un objeto Workflow a partir de datos de Firestore.
 */
export function parseWorkflow(id: string, data: Record<string, unknown>): Workflow {
  const normalizedGraph = normalizeWorkflowGraph(
    Array.isArray(data.nodes) ? data.nodes : [],
    Array.isArray(data.edges) ? data.edges : []
  );

  if (normalizedGraph.issues.length > 0) {
    console.warn(
      `Workflow ${id} contiene nodos o edges legacy/incompatibles. Se normalizará con ${normalizedGraph.issues.length} incidencia(s).`,
      normalizedGraph.issues
    );
  }

  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    name: typeof data.name === 'string' ? data.name : 'Workflow sin nombre',
    description: typeof data.description === 'string' ? data.description : undefined,
    nodes: normalizedGraph.nodes as Node[],
    edges: normalizedGraph.edges as Edge[],
    // H-F017: Validar elementos individuales de excelFiles (estructura basica)
    excelFiles: Array.isArray(data.excelFiles)
      ? (data.excelFiles as unknown[]).filter(
          (f): f is ExcelFileMetadata =>
            typeof f === 'object' && f !== null &&
            typeof (f as Record<string, unknown>).name === 'string'
        )
      : undefined,
    // H-F010: Usar safeTimestamp para validar Timestamps en vez de cast directo
    createdAt: safeTimestamp(data.createdAt) ?? ({} as Timestamp),
    updatedAt: safeTimestamp(data.updatedAt) ?? ({} as Timestamp),
    lastExecutedAt: safeTimestamp(data.lastExecutedAt) ?? undefined,
    executionCount: typeof data.executionCount === 'number' ? data.executionCount : 0,
    successCount: typeof data.successCount === 'number' ? data.successCount : 0,
    failureCount: typeof data.failureCount === 'number' ? data.failureCount : 0,
    isPublic: typeof data.isPublic === 'boolean' ? data.isPublic : false,
    // H-F017: Validar que cada tag sea string
    tags: Array.isArray(data.tags) ? (data.tags as unknown[]).filter((t): t is string => typeof t === 'string') : [],
  };
}

/**
 * Elimina recursivamente propiedades con valor `undefined` de un objeto.
 * Firestore rechaza documentos que contengan `undefined` en cualquier nivel.
 */
function stripUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined) as T;
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (value !== undefined) {
      clean[key] = stripUndefined(value);
    }
  }
  return clean as T;
}

class WorkflowsService {
  private getWorkflowsCollection() {
    return collection(db, 'workflows');
  }

  private getWorkflowDoc(id: string) {
    return doc(db, 'workflows', id);
  }

  // Crear nuevo workflow
  async createWorkflow(userId: string, input: CreateWorkflowInput): Promise<string> {
    const workflowsRef = this.getWorkflowsCollection();
    
    const workflowData = {
      userId,
      name: input.name.trim() || 'Workflow sin nombre',
      description: input.description?.trim() || '',
      nodes: input.nodes,
      edges: input.edges,
      excelFiles: input.excelFiles || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      executionCount: 0,
      successCount: 0,
      failureCount: 0,
      isPublic: false,
      tags: input.tags || [],
    };

    const docRef = await addDoc(workflowsRef, stripUndefined(workflowData));
    return docRef.id;
  }

  // Obtener workflow por ID
  async getWorkflow(id: string): Promise<Workflow | null> {
    const docRef = this.getWorkflowDoc(id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return parseWorkflow(docSnap.id, docSnap.data() as Record<string, unknown>);
  }

  // Obtener todos los workflows de un usuario
  async getUserWorkflows(userId: string): Promise<Workflow[]> {
    const workflowsRef = this.getWorkflowsCollection();
    const q = query(
      workflowsRef,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) =>
      parseWorkflow(doc.id, doc.data() as Record<string, unknown>)
    );
  }

  // Obtener workflows recientes
  async getRecentWorkflows(userId: string, count: number = 5): Promise<Workflow[]> {
    const workflowsRef = this.getWorkflowsCollection();
    const q = query(
      workflowsRef,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) =>
      parseWorkflow(doc.id, doc.data() as Record<string, unknown>)
    );
  }

  // Actualizar workflow
  async updateWorkflow(id: string, input: UpdateWorkflowInput): Promise<void> {
    const docRef = this.getWorkflowDoc(id);
    const updateData: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (input.name !== undefined) {
      updateData.name = input.name.trim() || 'Workflow sin nombre';
    }
    if (input.description !== undefined) {
      updateData.description = input.description?.trim() || '';
    }
    if (input.nodes !== undefined) {
      updateData.nodes = input.nodes;
    }
    if (input.edges !== undefined) {
      updateData.edges = input.edges;
    }
    if (input.tags !== undefined) {
      updateData.tags = input.tags;
    }
    if (input.excelFiles !== undefined) {
      updateData.excelFiles = input.excelFiles;
    }

    await updateDoc(docRef, stripUndefined(updateData));
  }

  // Eliminar workflow
  // [H-B015] Manejo de error permission-denied con mensaje amigable.
  async deleteWorkflow(id: string): Promise<void> {
    const docRef = this.getWorkflowDoc(id);
    try {
      await deleteDoc(docRef);
    } catch (error: unknown) {
      // Detectar error de permisos de Firestore para dar mensaje amigable
      const firebaseError = error as { code?: string };
      if (firebaseError.code === 'permission-denied') {
        throw new Error('No tienes permiso para eliminar este workflow.');
      }
      throw error;
    }
  }

  // Incrementar contador de ejecución (operación atómica con increment)
  async incrementExecutionCount(id: string, success: boolean): Promise<void> {
    const docRef = this.getWorkflowDoc(id);

    // H-B007: Solo enviar increment para el contador que corresponda, sin increment(0) innecesario
    const counters: Record<string, unknown> = {
      executionCount: increment(1),
      lastExecutedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (success) {
      counters.successCount = increment(1);
    } else {
      counters.failureCount = increment(1);
    }
    await updateDoc(docRef, counters);
  }
}

export const workflowsService = new WorkflowsService();
